import { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Button, ButtonGroup } from '@upmc/ims.component-library';

import axiosInstance from '../../services/axios/axios';
import Edit from './containers/Edit';
import SubmissionSummary from './containers/SubmissionSummary';
import AccountDetailData from './components/AccountDetailData';
import UARLayout from './containers/Layout';
import Icon from '../../components/UI/Icon/Icon';
import { WrapImsPacket } from '../../store/utility';
import classes from './UserAccessReview.module.scss';
import * as actions from '../../store/uar/actions';
import * as utils from '../../utils';
import * as lifecycleWrapper from './util/lifecycleWrapper';
import {
	SUBMISSION_LOADING_BULK_MESSAGE,
	SUBMISSION_LOADING_TITLE,
} from './util/constants';

/**
 * User Access Review Validate Component
 * @param {object} props
 */
const Validate = props => {
	/**
	 * Extract needed variables from the props provided by the `Scaffold`
	 */
	const {
		dispatch,
		loggedInUser,
		error,
		errorMessage,
		loading,
		setLoading,
		usersFound,
		bulkSelections,
		bulkFlow,
		esoFiId,
		canPerformActions,
		setCanPerformActions,
		loggedInEsoFiId,
	} = props;

	//STATE

	/**
	 * (set)Profile - if not set already, used to get and save user info from API
	 */
	const [profile, setProfile] = useState({});

	/**
	 * (set)Submitted - flag to indicate if submit button has been clicked
	 */
	const [submitted, setSubmitted] = useState(false);

	/**
	 * (set)SubmittedSuccessfully - flag to indicate if submission was successful
	 */
	const [submittedSuccessfully, setSubmittedSuccessfully] = useState(false);

	//LIFECYCLE

	/**
	 * Validate Page - check permissions
	 */
	useEffect(() => {
		if (profile && Object.keys(profile).length > 0 && loggedInEsoFiId) {
			let valid = true;
			Object.keys(profile).forEach(key => {
				//assumption:  only two groups can perform actions:  supervisors and delegated owners
				const supervisor = profile[key].details.supervisorEsoFiIds
					? profile[key].details.supervisorEsoFiIds.includes(
							loggedInEsoFiId
					  )
					: false;
				const delegatedOwner =
					profile[key].details.isDelegated &&
					profile[key].details.delegatedOwnerEsoFiId
						? profile[key].details.isDelegated &&
						  profile[key].details.delegatedOwnerEsoFiId ===
								loggedInEsoFiId
						: false;
				valid = valid && (supervisor || delegatedOwner);
			});
			setCanPerformActions(valid);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loggedInEsoFiId, profile, bulkSelections]);

	useEffect(() => {
		if (esoFiId) {
			setLoading(true);
			getAccountDetails(esoFiId)
				.then(result => {
					const newProfile = { ...profile };
					newProfile[esoFiId] = result[esoFiId];
					setProfile(newProfile);
					setLoading(false);
				})
				.catch(error => {
					setLoading(false);
				});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [esoFiId]);

	useEffect(() => {
		if (bulkFlow && usersFound) {
			const newProfile = {};
			let promises = [];
			bulkSelections.forEach(selection => {
				if (!newProfile[selection.esoFiId]) {
					promises.push(getAccountDetails(selection.esoFiId));
				}
			});
			setLoading(true);
			Promise.all(promises).then(results => {
				results.forEach(selection => {
					Object.keys(selection).forEach(key => {
						if (!newProfile[key]) {
							newProfile[key] = selection[key];
						}
					});
				});
				setProfile(newProfile);
				setLoading(false);
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [bulkSelections, bulkFlow, usersFound]);

	const getAccountDetails = esoFiId => {
		return new Promise((resolve, reject) => {
			let targetProfile = null;
			let newDetails = { ...profile.details };
			let newApplications = { ...profile.applications };
			let newMobileDevices = { ...profile.mobileDevices };
			let newBadges = { ...profile.badges };
			let newServiceAccounts = { ...profile.serviceAccounts };

			let requestGetUserInfo = axiosInstance.get(
				'/api/UserAccessReview/GetUserInfo/' + esoFiId
			);
			let requestGetUserApps = axiosInstance.get(
				'/api/UserAccessReview/GetUserApps/' + esoFiId
			);
			let requestGetUserMobileDevices = axiosInstance.get(
				'/api/UserAccessReview/GetUserMobileDevices/' + esoFiId
			);
			let requestGetUserBadges = axiosInstance.get(
				'/api/UserAccessReview/GetUserBadges/' + esoFiId
			);
			let requestGetUserServiceAccounts = axiosInstance.get(
				'/api/UserAccessReview/GetUserServiceAccounts/' + esoFiId
			);
			axios
				.all([
					requestGetUserInfo,
					requestGetUserApps,
					requestGetUserMobileDevices,
					requestGetUserBadges,
					requestGetUserServiceAccounts,
				])
				.then(
					axios.spread((...responses) => {
						const responseGetUserInfo = responses[0];
						const responseGetUserApps = responses[1];
						const responseGetUserMobileDevices = responses[2];
						const responseGetUserBadges = responses[3];
						const responseGetUserServiceAccounts = responses[4];

						switch (responseGetUserInfo.status) {
							case 200:
								if (responseGetUserInfo.data.responseCode) {
									const responseCode =
										responseGetUserInfo.data.responseCode;
									let data = responseGetUserInfo.data.payload;
									switch (responseCode) {
										case 200:
											newDetails = data;
											break;
										default:
											break;
									}
								}
								break;

							default:
								break;
						}

						switch (responseGetUserApps.status) {
							case 200:
								if (responseGetUserApps.data.responseCode) {
									const responseCode =
										responseGetUserApps.data.responseCode;
									let data = responseGetUserApps.data.payload;
									switch (responseCode) {
										case 200:
											newApplications = data;
											break;
										default:
											break;
									}
								}
								break;

							default:
								break;
						}

						switch (responseGetUserMobileDevices.status) {
							case 200:
								if (
									responseGetUserMobileDevices.data
										.responseCode
								) {
									const responseCode =
										responseGetUserMobileDevices.data
											.responseCode;
									let data =
										responseGetUserMobileDevices.data
											.payload;
									switch (responseCode) {
										case 200:
											newMobileDevices = data;
											break;
										default:
											break;
									}
								}
								break;

							default:
								break;
						}

						switch (responseGetUserBadges.status) {
							case 200:
								if (responseGetUserBadges.data.responseCode) {
									const responseCode =
										responseGetUserBadges.data.responseCode;
									let data =
										responseGetUserBadges.data.payload;
									switch (responseCode) {
										case 200:
											newBadges = data;
											break;
										default:
											break;
									}
								}
								break;

							default:
								break;
						}

						switch (responseGetUserServiceAccounts.status) {
							case 200:
								if (
									responseGetUserServiceAccounts.data
										.responseCode
								) {
									const responseCode =
										responseGetUserServiceAccounts.data
											.responseCode;
									let data =
										responseGetUserServiceAccounts.data
											.payload;
									switch (responseCode) {
										case 200:
											newServiceAccounts = data;
											break;
										default:
											break;
									}
								}
								break;

							default:
								break;
						}

						targetProfile = {
							details: newDetails,
							applications: newApplications,
							mobileDevices: newMobileDevices,
							badges: newBadges,
							serviceAccounts: newServiceAccounts,
							expanded: false,
						};
					})
				)
				.then(() => {
					let newProfile = { ...profile };
					newProfile[esoFiId] = targetProfile;
					resolve(newProfile);
				})
				.catch(errors => {
					reject(errors);
				});
		});
	};

	//FUNCTIONS
	const removeHandler = member => {
		const newProfile = { ...profile };
		delete newProfile[member.details.esoFiId];
		setProfile(newProfile);
		dispatch(
			actions.removeBulkSelection({
				payload: member.details,
			})
		);
	};

	const expandHandler = esoFiId => {
		let newProfile = { ...profile };
		if (newProfile[esoFiId]) {
			newProfile[esoFiId].expanded = !newProfile[esoFiId].expanded;
		}
		setProfile(newProfile);
	};

	/**
	 * actions to take upon form submission
	 */
	const submitHandler = () => {
		if (true) {
			let targets = [];
			if (bulkSelections && bulkSelections.length > 0) {
				targets = bulkSelections;
			} else {
				Object.keys(profile).forEach(key => {
					targets.push(profile[key].details);
				});
			}
			const payload = {
				loggedInUser: loggedInUser.loggedInUser,
				targets: targets,
			};

			setSubmitted(true);
			axiosInstance
				.post(
					'/api/UserAccessReview/Validation',
					WrapImsPacket(payload)
				)
				.then(response => {
					switch (response.status) {
						case 200:
							if (response.data.responseCode) {
								const responseCode = response.data.responseCode;
								switch (responseCode) {
									case 200:
										setSubmittedSuccessfully(true);
										break;
									default:
										setSubmitted(false);
										setSubmittedSuccessfully(false);
										break;
								}
							}
							break;

						default:
							setSubmitted(false);
							setSubmittedSuccessfully(false);
							break;
					}
				})
				.catch(errors => {
					setSubmitted(false);
					setSubmittedSuccessfully(false);
				});
		}
		//TODO|BPK: else post message
	};

	/**
	 * actions to take if cancelled
	 */
	const cancelHandler = () => {
		props.history.goBack();
	};

	//RENDER

	/**
	 * Form components
	 */
	const formOutput = (
		<div className={classes['list-container--scrollable']}>
			{profile
				? Object.keys(profile).map((key, index) => {
						const [
							details,
							applications,
							mobileDevices,
							badges,
							serviceAccounts,
							expanded,
						] = [
							profile[key].details,
							profile[key].applications,
							profile[key].mobileDevices,
							profile[key].badges,
							profile[key].serviceAccounts,
							profile[key].expanded,
						];
						const name =
							details.firstName +
							' ' +
							(details.middleInitial.length > 0
								? details.middleInitial + ' '
								: '') +
							details.lastName;

						return (
							<div
								id='uar-validate-list'
								key={'validate_list-' + key}
							>
								<div
									className={classes['validate__container']}
									key={index}
								>
									<div
										className={
											classes['validate__container--data']
										}
									>
										<section id='uar-target-name-container'>
											<span>Name:</span>{' '}
											<span id='uar-target-name'>
												{name}
											</span>
										</section>
										<section id='uar-target-email-container'>
											<span>Email:</span>{' '}
											<span id='uar-target-email'>
												{details.email
													? details.email
													: 'N/A'}
											</span>
										</section>
										<section id='uar-target-ntid-container'>
											<span>NTID:</span>{' '}
											<span id='uar-target-ntid'>
												{details.ntid
													? details.ntid
													: 'N/A'}
											</span>
										</section>
										<section id='uar-target-emptype-container'>
											<span>Employee Type:</span>{' '}
											<span id='uar-target-emptype'>
												{lifecycleWrapper.accountTypeLookup(
													details.type
												)}
											</span>
										</section>
										<section id='uar-target-apps-container'>
											<span>Application Count:</span>{' '}
											<span id='uar-target-apps'>
												{applications.length}
											</span>
										</section>
										<section id='uar-target-mobile-container'>
											<span>Mobile Devices Count:</span>{' '}
											<span id='uar-target-mobile'>
												{mobileDevices.length}
											</span>
										</section>
										<section id='uar-target-badge-container'>
											<span>Badges Count:</span>{' '}
											<span id='uar-target-badge'>
												{badges.length}
											</span>
										</section>
										<section id='uar-target-svcaccts-container'>
											<span>Service Account Count:</span>{' '}
											<span id='uar-target-svcaccts'>
												{serviceAccounts.length}
											</span>
										</section>
									</div>
									<ButtonGroup 
										vertical 
										className={
											classes['validate__container--btn']
										}>
											<Button
												disabled={false}
												onClick={() =>
													expandHandler(
														details.esoFiId
													)
												}
												variant='outlined'
												endIcon={
													<Icon
														icon={
															expanded
																? 'unfold_less'
																: 'unfold_more'
														}
													/>
												}
											>
												{
													expanded
														? 'Less Info'
														: 'More Info'
												}
											</Button>
											<Link
												to={{
													pathname:
														'/UserAccessReview/AccountDetails',
													search:
														'?esoFiId=' +
														details.esoFiId,
												}}
												className={utils.concatenateClassNames(
													['button__link--reset'],
													classes
												)}
											>
												<Button
													disabled={false}
													endIcon={
														<Icon icon='edit' />
													}
												>
													Modify and Validate
												</Button>
											</Link>
											{bulkFlow ? (
												<Button
													disabled={
														Object.keys(profile)
															.length <= 1
													}
													onClick={() =>
														removeHandler(
															profile[key]
														)
													}
													variant='outlined'
													endIcon={
														<Icon icon='remove_circle_outline' />
													}
												>
													Remove
												</Button>
											) : null}
										</ButtonGroup>
								</div>
								{expanded ? (
									<AccountDetailData
										dispatch={dispatch}
										loggedInUser={loggedInUser}
										error={error}
										errorMessage={errorMessage}
										loading={loading}
										canPerformActions={false}
										profile={profile[key]}
										// Validate `profile` structure different than `AccountDetailData` `profile` structure
										setProfile={profileAccountDetailData => {
											let profileCopy = { ...profile };
											profileCopy[key] =
												profileAccountDetailData;
											setProfile(profileCopy);
										}}
										applications={applications}
										mobileDevices={mobileDevices}
										badges={badges}
										serviceAccounts={serviceAccounts}
										bulkSelections={bulkSelections}
										readOnly={true}
									/>
								) : null}
							</div>
						);
				  })
				: null}
		</div>
	);

	const formOutputSummary = (
		<div className={classes['list-container--scrollable']}>
			{profile
				? Object.keys(profile).map((key, index) => {
						const [details] = [profile[key].details];
						const name =
							details.firstName +
							' ' +
							(details.middleInitial.length > 0
								? details.middleInitial + ' '
								: '') +
							details.lastName;

						return (
							<div
								className={classes['validate__container']}
								key={index}
							>
								<div
									className={
										classes['validate__container--data']
									}
								>
									<section>
										<span>Name:</span> {name}
									</section>
									<section>
										<span>Email:</span>{' '}
										{details.email ? details.email : 'N/A'}
									</section>
									<section>
										<span>NTID:</span>{' '}
										{details.ntid ? details.ntid : 'N/A'}
									</section>
									<section>
										<span>Employee Type:</span>{' '}
										{lifecycleWrapper.accountTypeLookup(
											details.type
										)}
									</section>
									<section>
										<span>Application Count:</span>{' '}
										{profile[key].applications &&
											profile[key].applications.length}
									</section>
									<section>
										<span>Mobile Devices Count:</span>{' '}
										{profile[key].mobileDevices &&
											profile[key].mobileDevices.length}
									</section>
									<section>
										<span>Badges Count:</span>{' '}
										{profile[key].badges &&
											profile[key].badges.length}
									</section>
									<section>
										<span>Service Account Count:</span>{' '}
										{profile[key].serviceAccounts &&
											profile[key].serviceAccounts.length}
									</section>
								</div>
							</div>
						);
				  })
				: null}
		</div>
	);

	/**
	 * return JSX to render
	 */
	return (
		<UARLayout
			title='Review Employee Access'
			loading={loading}
			error={error}
			errorMessage={errorMessage}
			canPerformActions={canPerformActions}
		>
			{submittedSuccessfully ? (
				<SubmissionSummary
					profile={
						bulkSelections && bulkSelections.length > 0
							? null
							: profile[esoFiId].details
					}
				>
					<div>Validation submitted for:</div>
					{formOutputSummary}
				</SubmissionSummary>
			) : (
				<Edit
					cancellable={{
						handler: cancelHandler,
						disabled: submitted,
					}}
					submittable={{
						handler: submitHandler,
						label:
							bulkSelections && bulkSelections.length > 0
								? 'Validate All'
								: 'Validate',
						disabled: submitted,
					}}
					loading={{
						isLoading: submitted,
						title: SUBMISSION_LOADING_TITLE,
						message: bulkFlow
							? SUBMISSION_LOADING_BULK_MESSAGE
							: (profile[esoFiId]?.serviceAccounts?.length ?? 0) >
							  50
							? 'The account you are validating is associated with many service accounts which can take more time to process.'
							: null,
					}}
					profile={
						bulkSelections && bulkSelections.length > 0
							? null
							: profile &&
							  profile[esoFiId] &&
							  profile[esoFiId].details
							? profile[esoFiId].details
							: null
					}
				>
					{formOutput}
				</Edit>
			)}
		</UARLayout>
	);
};

export default Validate;
